import {
  bufferTime,
  catchError,
  debounceTime,
  filter,
  from,
  groupBy,
  map,
  mergeMap,
  type Observable,
  of,
  share,
  Subject,
  take,
} from 'rxjs';
import { _isNotEmpty } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import { type InVivoValidatorResult } from '@/support/hotValidator.util';
import { ApiService } from '@/support/ApiService';
import { apiErrorToast } from '@/components/UI/ApiErrorToast/ApiErrorToast';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

/**
 * RxJS-based request deduplication for validation API calls.
 *
 * ## Purpose
 * Prevents "request storms" when multiple validators call the API simultaneously with
 * identical parameters (e.g., 100 spreadsheet cells validating on load). This implementation
 * provides **deduplication only**, not caching - sequential requests will make new API calls.
 * Long-term caching is handled by HTTP cache headers (Cache-Control) set by the server.
 *
 * ## How It Works (Fan-In/Fan-Out Pattern with Debouncing)
 * 1. **Fan-In**: Multiple validation requests with identical parameters are pushed to
 *    #validationQueue$ and grouped by their cache key via `groupBy()`
 * 2. **Batching Window**: `debounceTime(50)` waits 50ms after the last request in a group
 *    to collect near-concurrent requests (e.g., cells rendering in quick succession)
 * 3. **Single API Call**: After the debounce period, only one API call is made per group
 * 4. **Fan-Out**: The API response is broadcast to all subscribers waiting for that cache key
 * 5. **Group Lifecycle**: Groups close after processing via `take(1)`, ensuring no client-side
 *    result caching. Subsequent requests create new groups and new API calls.
 *
 * ## Deduplication Scope
 * - ✅ **Concurrent requests** (same key, within 50ms window): 1 API call shared by all
 * - ✅ **Near-concurrent requests** (same key, < 50ms apart): Batched into 1 API call
 * - ❌ **Sequential requests** (same key, > 50ms after previous): New API call each time
 *
 * ## Example
 * ```typescript
 * // 100 cells validate within 50ms window with same options
 * const promises = Array(100).fill(null).map(() =>
 *   cache.validate(glossaryId, new Set(['option1', 'option2']))
 * );
 * await Promise.all(promises);
 * // Result: Only 1 API call made after 50ms debounce, all 100 cells receive the same result
 *
 * // 100ms later, another cell validates with same options (outside debounce window)
 * await cache.validate(glossaryId, new Set(['option1', 'option2']));
 * // Result: New API call (HTTP cache will prevent redundant network request)
 * ```
 *
 * @see https://rxjs.dev/api/operators/groupBy - For understanding the grouping behavior
 * @see https://rxjs.dev/api/operators/debounceTime - For understanding the batching window
 * @see https://rxjs.dev/api/operators/share - For understanding result multicasting without caching
 */
export class ValidationRequestCache {
  /**
   * Queue of validation requests. Requests are pushed here and then processed
   * by the #validationResults$ pipeline.
   * @private
   */
  #validationQueue$ = new Subject<{ key: string; cacheKey: string | number; values: Array<string> }>();

  /**
   * Observable stream that processes validation requests and emits results.
   *
   * Pipeline:
   * 1. `groupBy(key)` - Creates separate Observable for each unique cache key
   * 2. `debounceTime(50)` - Waits 50ms after last event to batch near-concurrent requests
   * 3. `take(1)` - Each group processes only the last request after debounce, ignores earlier duplicates
   * 4. `mergeMap` - Converts the request into an API call (Promise → Observable)
   * 5. `share()` - Multicasts results to active subscribers only (no buffering)
   *
   * Note: Groups close after `take(1)`, so subsequent requests with same key (after debounce
   * window) create new groups and trigger new API calls. `share()` provides pure deduplication
   * without client-side caching - sequential requests will make new API calls.
   * @private
   */
  #validationResults$: Observable<{ key: string; result: InVivoValidatorResult }> = this.#validationQueue$.pipe(
    groupBy(({ key }) => key),
    mergeMap((group$) =>
      group$.pipe(
        debounceTime(50),
        take(1),
        mergeMap(({ key, cacheKey, values }) =>
          from(this.#validateFunction(cacheKey, values)).pipe(
            map((result) => ({ key, result })),
            catchError(() => of({ key, result: { valid: false } as InVivoValidatorResult }))
          )
        )
      )
    ),
    share()
  );

  /**
   * Creates a new ValidationRequestCache instance.
   *
   * @param validateFunction - Function that performs the actual validation API call.
   *   Takes a cacheKey (string | number) and array of values, returns validation result.
   *   The cacheKey scope is application-defined (e.g., a metadata glossary id, or a
   *   resource api_id). This function should handle its own error cases and return
   *   appropriate results.
   */
  #validateFunction: (cacheKey: string | number, values: Array<string>) => Promise<InVivoValidatorResult>;
  constructor(validateFunction: (cacheKey: string | number, values: Array<string>) => Promise<InVivoValidatorResult>) {
    this.#validateFunction = validateFunction;
  }

  /**
   * Validates a set of option values against a metadata glossary.
   *
   * This method deduplicates near-concurrent identical requests using the fan-in/fan-out
   * pattern with a 50ms debounce window:
   * - Multiple calls within 50ms with same parameters share a single API call
   * - Calls outside the 50ms window make new API calls
   *
   * The 50ms debounce adds imperceptible latency (< 1 frame at 60fps) but significantly
   * improves deduplication by catching requests from:
   * - Same React render cycle
   * - Sequential useEffect chains
   * - Async key generation delays
   * - Browser event loop timing variations
   *
   * @param metadataGlossaryId {number} - The metadata glossary ID to validate against
   * @param values {Set<string>} - Set of option values to validate
   * @returns {Promise<InVivoValidatorResult>} Promise resolving to validation result: { valid: true } or { valid: false, message: string }
   *
   * @example
   * ```typescript
   * // Multiple validations within debounce window (deduped into 1 API call)
   * const results = await Promise.all([
   *   cache.validate(123, new Set(['option1', 'option2'])),  // T=0ms
   *   cache.validate(123, new Set(['option1', 'option2'])),  // T=5ms
   *   cache.validate(123, new Set(['option1', 'option2'])),  // T=10ms
   * ]);
   * // API call triggered at T=60ms (50ms after last request)
   * // All 3 receive same result from single API call
   *
   * // Later validation with same params (new API call, but HTTP cache may serve it)
   * await new Promise(resolve => setTimeout(resolve, 100)); // Wait > 50ms
   * const result = await cache.validate(123, new Set(['option1', 'option2']));
   * // New API call triggered (outside debounce window)
   * ```
   */
  async validate(cacheKey: string | number, values: Set<string>): Promise<InVivoValidatorResult> {
    const { key, values: sortedValues } = await ValidationRequestCache.generateKey(cacheKey, values);

    return await new Promise((resolve, reject) => {
      this.#validationResults$
        .pipe(
          filter((result) => result.key === key),
          take(1),
          map(({ result }) => result)
        )
        .subscribe({ next: resolve, error: reject });

      this.#validationQueue$.next({ key, cacheKey, values: sortedValues });
    });
  }

  /**
   * Generates a deterministic SHA-1 hash cache key from validation parameters and returns sorted values.
   *
   * This method serves dual purposes:
   * 1. Generates a deterministic cache key for request deduplication
   * 2. Returns sorted values array to ensure consistent ordering for API calls
   *
   * The cache key must be:
   * - **Deterministic**: Same inputs always produce same key
   * - **Order-independent**: Set(['a','b']) and Set(['b','a']) produce identical key
   * - **Unique**: Different glossaries or options produce different keys
   * - **Collision-resistant**: SHA-1 provides sufficient entropy for this use case
   *
   * @param metadataGlossaryId {number} - The metadata glossary ID
   * @param values {Set<string>} - Set of option values (order doesn't matter, will be sorted internally)
   * @returns {Promise<{ key: string, values: Array<string> }>} Promise resolving to object with:
   *   - `key`: 40-character hex string (SHA-1 hash) for cache deduplication
   *   - `values`: Sorted array of option values for consistent API calls
   *
   * @example
   * ```typescript
   * const { key, values } = await ValidationRequestCache.generateKey(
   *   123,
   *   new Set(['b', 'a', 'c'])
   * );
   * console.log(key);    // "a1b2c3d4e5f6..." (40 hex characters)
   * console.log(values); // ['a', 'b', 'c'] (sorted)
   *
   * // Order-independent: same inputs in different order produce same key
   * const result1 = await ValidationRequestCache.generateKey(123, new Set(['a', 'b']));
   * const result2 = await ValidationRequestCache.generateKey(123, new Set(['b', 'a']));
   * console.assert(result1.key === result2.key); // true
   * ```
   */
  static async generateKey(
    cacheKey: string | number,
    values: Set<string>
  ): Promise<{ key: string; values: Array<string> }> {
    // Sort values to ensure order-independence for cache key generation
    const sortedValues = Array.from(values).sort();
    // Concatenate cache key and sorted values for hashing
    const keyBuffer = new TextEncoder().encode(`${cacheKey}|${sortedValues.join('|')}`);
    // Generate SHA-1 hash
    const hashBuffer = await crypto.subtle.digest('SHA-1', keyBuffer);
    // Convert hash bytes to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return { key: hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''), values: sortedValues };
  }
}

/**
 * Singleton instance for metadata glossary lookup option validation.
 *
 * This instance is configured to call the validate-lookup-options API endpoint
 * and is used by multiSelectInVivoValidator to deduplicate concurrent validation
 * requests across all spreadsheet cells.
 *
 * ## Configuration
 * - **API Endpoint**: `GET /api/v1/metadata-glossary/{metadataGlossaryId}/validate-lookup-options`
 * - **Debounce Window**: 50ms (batches near-concurrent requests)
 * - **Error Handling**: Errors are caught and return `{ valid: false }` instead of throwing
 * - **Toast Suppression**: API errors don't show toast notifications (onError: false)
 *
 * ## Usage
 * This singleton is imported by multiSelectInVivoValidator and automatically
 * deduplicates validation requests across all instances of the validator.
 *
 * @example
 * ```typescript
 * // Used by multiSelectInVivoValidator
 * const result = await validationCache.validate(
 *   optionsLookupId,
 *   selectedOptionsSet
 * );
 * // Returns: { valid: true } or { valid: false, message: string }
 * ```
 */
export const validationCache = new ValidationRequestCache((metadataGlossaryId, options) => {
  return ApiService.call({
    endpoint: 'GET /api/v1/metadata-glossary/{metadataGlossaryId}/validate-lookup-options',
    path: { metadataGlossaryId: metadataGlossaryId as number },
    query: { option: options },
    options: { onError: { capture: false, throw: false, toast: false, slug: 'lookup-option-validator' } },
  })
    .then((response) => {
      if (response.type === 'success') {
        return response.body.valid ? { valid: true } : { valid: false, message: 'Invalid options' };
      }
      return { valid: false, message: 'Could not verify' };
    })
    .catch(() => ({ valid: false, message: 'Could not verify' })) as Promise<InVivoValidatorResult>;
});

/**
 * Singleton instance for sample ID uniqueness validation.
 *
 * Used by the bulk sample edit screen to dedupe near-concurrent uniqueness checks
 * (per-cell validators that all fire within the same render pass / paste burst).
 *
 * ## Configuration
 * - **API Endpoint**: `GET /api/v1/samples/{sampleApiId}/unique/{sampleId}`
 * - **cacheKey**: the sample's `api_id` (string)
 * - **values**: a Set containing the proposed `sample_id` (single element)
 * - **Debounce Window**: 50ms (inherited from ValidationRequestCache)
 * - **Error Handling**: 4xx/5xx (including 429) → `{ valid: false, message: 'Could not verify' }`
 */
export const sampleIdValidationCache = new ValidationRequestCache((sampleApiId, values) => {
  const [sampleId] = values;
  return ApiService.call({
    endpoint: 'GET /api/v1/samples/{sampleApiId}/unique/{sampleId}',
    path: { sampleApiId: String(sampleApiId), sampleId },
    options: { onError: { capture: false, throw: false, toast: false, slug: 'sample-id-uniqueness-validator' } },
  })
    .then((response) => {
      if (response.type === 'success') {
        return response.body.success === true ? { valid: true } : { valid: false };
      }
      return { valid: false, message: 'Could not verify' };
    })
    .catch(() => ({ valid: false, message: 'Could not verify' })) as Promise<InVivoValidatorResult>;
});

/**
 * Validates many distinct values in a single API call, returning one result per value
 * keyed by the normalised value.
 */
export type BatchValidateFunction = (values: Array<string>) => Promise<Map<string, InVivoValidatorResult>>;

const COULD_NOT_VERIFY: InVivoValidatorResult = { valid: false, message: 'Could not verify' };

const TREATMENT_NAME_VALIDATION_TOAST_ID = 'treatment-name-validation-failed';

/**
 * RxJS-based request aggregation for bulk validation API calls.
 *
 * Where {@link ValidationRequestCache} dedupes requests that share an *identical* key,
 * this variant aggregates *distinct* values: every value enqueued within the buffer window
 * is collected into one array, passed to a single bulk API call, and the response is fanned
 * back out to each waiting caller by normalised value.
 *
 * ## How It Works (Fan-In/Fan-Out Pattern with Buffering)
 * 1. **Fan-In**: `bufferTime(window)` collects every value enqueued during the window
 * 2. **Dedupe**: values are normalised (trimmed, lower-cased) and de-duplicated
 * 3. **Single API Call**: one `batchValidate` call receives the whole batch
 * 4. **Fan-Out**: each caller resolves from the returned Map using its own normalised value
 *
 * `bufferTime` rather than `debounceTime`: the window must close a fixed interval after the
 * first enqueue, otherwise a large paste keeps resetting the window and no request is sent.
 *
 * @example
 * ```typescript
 * // 50 spreadsheet cells validate distinct names within the window
 * await Promise.all(names.map((name) => cache.validate(name)));
 * // Result: 1 API call carrying all 50 names; each cell receives its own result
 * ```
 */
export class BufferedValidationRequestCache {
  #queue$ = new Subject<string>();

  #batchValidate: BatchValidateFunction;

  /**
   * Emits one batch per buffer window. `requested` is the set of normalised values sent to
   * the API and is what callers match on — matching on `results` instead would hang any
   * caller whose value the response omitted. Failures emit an empty result map so those
   * callers still resolve, and the stream survives for later windows; the failure is captured
   * first, since anything reaching here is a defect the `batchValidate` body did not handle.
   */
  #results$: Observable<{ requested: Set<string>; results: Map<string, InVivoValidatorResult> }>;

  constructor(batchValidate: BatchValidateFunction, bufferWindowMs = 50) {
    this.#batchValidate = batchValidate;
    this.#results$ = this.#queue$.pipe(
      bufferTime(bufferWindowMs),
      filter((values) => _isNotEmpty(values)),
      map((values) => Array.from(new Set(values.map(BufferedValidationRequestCache.normalise)))),
      mergeMap((values) =>
        from(this.#batchValidate(values)).pipe(
          catchError((error) => {
            ExceptionHandler.captureException(
              new InVivoError('Batch validation request failed', {
                cause: error,
                slug: 'buffered-validation-batch',
                context: { valueCount: String(values.length) },
              })
            );
            return of(new Map<string, InVivoValidatorResult>());
          }),
          map((results) => ({ requested: new Set(values), results }))
        )
      ),
      share()
    );
  }

  /**
   * Queues a single value for validation and resolves once its batch responds.
   *
   * Resolves to `{ valid: false, message: 'Could not verify' }` when the batch fails or
   * when the response omits the requested value, so a caller can never hang.
   */
  async validate(value: string): Promise<InVivoValidatorResult> {
    const key = BufferedValidationRequestCache.normalise(value);

    return await new Promise((resolve, reject) => {
      this.#results$
        .pipe(
          filter(({ requested }) => requested.has(key)),
          take(1),
          map(({ results }) => results.get(key) ?? COULD_NOT_VERIFY)
        )
        .subscribe({ next: resolve, error: reject });

      this.#queue$.next(value);
    });
  }

  /**
   * Fan-out key for a value. Comparison is case-insensitive after trimming so that
   * requests, responses and callers agree regardless of how the value was typed.
   */
  static normalise(value: string): string {
    return value.trim().toLowerCase();
  }
}

/**
 * Singleton instance for treatment type glossary name uniqueness validation.
 *
 * ## Configuration
 * - **API Endpoint**: `GET /api/v1/team-glossary/treatment-types/validate`
 * - **Request**: `name` repeated once per value; the server collapses exact duplicates
 * - **Response**: `Array<{ name, valid }>` — results are matched back by name, not by
 *   position, because the server may return fewer entries than were requested
 * - **Buffer Window**: 50ms
 * - **Error Handling**: failures resolve to `{ valid: false, message: 'Could not verify' }`
 */
export const treatmentTypeNameValidationCache = new BufferedValidationRequestCache(async (names) => {
  const response = await ApiService.call({
    endpoint: 'GET /api/v1/team-glossary/treatment-types/validate',
    path: undefined,
    query: { name: names },
    options: { onError: { capture: false, throw: false, toast: false, slug: 'treatment-type-name-validator' } },
  });

  if (response.type !== 'success') {
    ExceptionHandler.captureException(
      new InVivoError('Treatment name validation endpoint failed', {
        cause: response.error,
        slug: 'treatment-type-name-validator',
        context: { nameCount: `${names.length}` },
      })
    );
    // Every affected cell shows only "Could not verify", so the toast is the sole place the
    // reason appears. A fixed id collapses the repeats a large paste would otherwise produce.
    apiErrorToast({
      title: 'Could not check treatment names',
      error: response.error,
      options: { toastId: TREATMENT_NAME_VALIDATION_TOAST_ID },
    });
    return new Map(names.map((name) => [name, COULD_NOT_VERIFY]));
  }

  return response.body.reduce<Map<string, InVivoValidatorResult>>((results, { name, valid }) => {
    results.set(
      BufferedValidationRequestCache.normalise(name),
      valid ? { valid: true } : { valid: false, message: 'Treatment name already exists in this team' }
    );
    return results;
  }, new Map());
});
